import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  TrendingUp, Map, FileCheck2, CalendarClock, ArrowRight, GraduationCap, Bot, Landmark, ExternalLink, LoaderCircle, TriangleAlert,
} from "lucide-react";
import { api } from "@/lib/api";
import { prefectureSite } from "@/lib/prefecture";
import { useAuth } from "@/context/AuthContext";
import { RenewalChecklistButton } from "@/components/dashboard/RenewalChecklistButton";
import { EASE } from "@/components/landing/Reveal";

const fade = (i) => ({
  initial: { opacity: 0, y: 22 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, delay: i * 0.07, ease: EASE },
});

const daysUntil = (d) => Math.ceil((new Date(`${d}T00:00:00`) - new Date()) / 86400000);

function Donut({ value }) {
  const r = 30;
  const c = 2 * Math.PI * r;
  return (
    <svg viewBox="0 0 72 72" className="h-16 w-16 -rotate-90">
      <circle cx="36" cy="36" r={r} fill="none" stroke="#EEF3FF" strokeWidth="8" />
      <circle
        cx="36" cy="36" r={r} fill="none" stroke="#1E3FAE" strokeWidth="8" strokeLinecap="round"
        strokeDasharray={c} strokeDashoffset={c - (c * value) / 100}
        style={{ transition: "stroke-dashoffset 1s cubic-bezier(0.22,1,0.36,1)" }}
      />
    </svg>
  );
}

export default function DashboardHome() {
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data)).catch(() => setData(false));
  }, []);

  if (data === null)
    return <div className="flex justify-center py-24"><LoaderCircle className="h-7 w-7 animate-spin text-bleu" /></div>;
  if (data === false)
    return <p className="py-24 text-center text-sm text-slate-500">Impossible de charger votre tableau de bord.</p>;

  const nextDeadline = data.upcoming_deadlines[0];
  const expiryDays = user?.titre_expiry ? Math.ceil((new Date(`${user.titre_expiry}T00:00:00`) - new Date()) / 86400000) : null;
  const showTitreAlert = expiryDays !== null && expiryDays <= 60;
  const expired = expiryDays !== null && expiryDays < 0;

  return (
    <div className="mx-auto max-w-6xl space-y-6" data-testid="dashboard-home">
      <motion.div {...fade(0)}>
        <h1 className="font-display text-2xl font-extrabold text-slate-900 sm:text-3xl">Tableau de bord</h1>
        <p className="mt-1 text-sm text-slate-500">Votre parcours d'intégration en un coup d'œil.</p>
      </motion.div>

      {showTitreAlert && (
        <motion.div {...fade(1)}
          className={`card-integra flex flex-wrap items-center gap-4 p-5 ${expired ? "!border-rouge/30 bg-rose-50/70" : "!border-amber-300/60 bg-amber-50/70"}`}
          data-testid="titre-expiry-alert">
          <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${expired ? "bg-rose-100" : "bg-amber-100"}`}>
            <TriangleAlert className={`h-5 w-5 ${expired ? "text-rouge" : "text-amber-600"}`} />
          </span>
          <div className="min-w-0 flex-1">
            <p className={`text-sm font-extrabold ${expired ? "text-rouge" : "text-amber-800"}`} data-testid="titre-expiry-alert-title">
              {expired
                ? `Votre titre de séjour a expiré depuis ${-expiryDays} jour${-expiryDays > 1 ? "s" : ""}`
                : `Votre titre de séjour expire dans ${expiryDays} jour${expiryDays > 1 ? "s" : ""}`}
            </p>
            <p className="mt-0.5 text-xs leading-relaxed text-slate-600">
              {user.titre_type} — {expired
                ? "déposez votre demande de renouvellement sans attendre auprès de votre préfecture."
                : "la demande de renouvellement se dépose entre 4 et 2 mois avant l'expiration. Préparez votre dossier dès maintenant."}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Link to="/dashboard/renouvellement"
              className="btn-secondary !px-5 !py-2.5 !text-xs" data-testid="titre-alert-guide-link">
              Guide ANEF pas à pas
            </Link>
            <RenewalChecklistButton />
          </div>
        </motion.div>
      )}

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <motion.div {...fade(1)} className="card-integra flex items-center gap-4 p-5" data-testid="stat-progression">
          <div className="relative">
            <Donut value={data.progression} />
            <span className="absolute inset-0 flex rotate-0 items-center justify-center text-sm font-extrabold text-bleu">{data.progression}%</span>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Progression</p>
            <p className="mt-0.5 text-sm font-bold text-slate-900">{data.steps_completed}/{data.steps_total} étapes terminées</p>
          </div>
        </motion.div>

        <motion.div {...fade(2)} className="card-integra p-5" data-testid="stat-next-step">
          <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-400"><Map className="h-3.5 w-3.5" /> Prochaine étape</p>
          <p className="mt-2 text-sm font-bold leading-snug text-slate-900">{data.next_step ? data.next_step.title : "Parcours terminé 🎉"}</p>
          <Link to="/dashboard/parcours" className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-bleu hover:underline">
            Voir mon parcours <ArrowRight className="h-3 w-3" />
          </Link>
        </motion.div>

        <motion.div {...fade(3)} className="card-integra p-5" data-testid="stat-documents">
          <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-400"><FileCheck2 className="h-3.5 w-3.5" /> Documents validés</p>
          <p className="mt-2 text-xl font-extrabold text-slate-900">{data.documents_checked}<span className="text-sm font-semibold text-slate-400">/{data.documents_total}</span></p>
          <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-slate-100">
            <div className="h-full rounded-full bg-emeraude transition-all duration-700" style={{ width: `${data.documents_total ? (data.documents_checked / data.documents_total) * 100 : 0}%` }} />
          </div>
        </motion.div>

        <motion.div {...fade(4)} className="card-integra p-5" data-testid="stat-deadline">
          <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-400"><CalendarClock className="h-3.5 w-3.5" /> Prochaine échéance</p>
          {nextDeadline ? (
            <>
              <p className="mt-2 text-sm font-bold leading-snug text-slate-900">{nextDeadline.title}</p>
              <p className={`mt-1.5 inline-flex rounded-full px-2.5 py-1 text-[11px] font-bold ${daysUntil(nextDeadline.date) <= 7 ? "bg-rose-50 text-rouge" : "bg-amber-50 text-ambre"}`}>
                Dans {daysUntil(nextDeadline.date)} jours
              </p>
            </>
          ) : (
            <p className="mt-2 text-sm text-slate-500">Aucune échéance à venir.</p>
          )}
        </motion.div>
      </div>

      {data.user.departement && (
        <motion.div {...fade(5)} className="card-integra flex flex-wrap items-center gap-4 p-5" data-testid="home-prefecture-card">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-bleu-light"><Landmark className="h-5 w-5 text-bleu" /></span>
          <div className="min-w-0 flex-1">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Ma préfecture de rattachement</p>
            <p className="mt-0.5 text-sm font-bold text-slate-900">{data.user.departement.name} ({data.user.departement.code}) — Préfecture : {data.user.departement.prefecture}</p>
          </div>
          <a href={prefectureSite(data.user.departement)} target="_blank" rel="noreferrer" className="btn-secondary !px-5 !py-2.5 !text-xs" data-testid="home-prefecture-link">
            Site officiel <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </motion.div>
      )}

      <div className="grid gap-4 lg:grid-cols-5">
        <motion.div {...fade(5)} className="card-integra p-6 lg:col-span-3" data-testid="home-parcours-preview">
          <div className="flex items-center justify-between">
            <h2 className="flex items-center gap-2 text-base font-bold text-slate-900"><TrendingUp className="h-4 w-4 text-bleu" /> Mon parcours</h2>
            <Link to="/dashboard/parcours" className="text-xs font-semibold text-bleu hover:underline" data-testid="home-parcours-link">Tout voir</Link>
          </div>
          <div className="mt-4 space-y-2.5">
            {data.next_step && (
              <div className="rounded-xl border border-bleu/15 bg-bleu-light p-4">
                <p className="text-[10px] font-bold uppercase tracking-wider text-bleu">En cours</p>
                <p className="mt-1 text-sm font-bold text-slate-900">{data.next_step.title}</p>
                <p className="mt-0.5 text-xs leading-relaxed text-slate-500">{data.next_step.desc}</p>
              </div>
            )}
            <div className="h-2 overflow-hidden rounded-full bg-slate-100">
              <div className="h-full rounded-full bg-gradient-to-r from-bleu to-bleu-accent transition-all duration-1000" style={{ width: `${data.progression}%` }} />
            </div>
            <p className="text-xs text-slate-400">{data.progression}% de votre feuille de route accomplie</p>
          </div>
        </motion.div>

        <motion.div {...fade(6)} className="card-integra p-6 lg:col-span-2" data-testid="home-deadlines-preview">
          <div className="flex items-center justify-between">
            <h2 className="flex items-center gap-2 text-base font-bold text-slate-900"><CalendarClock className="h-4 w-4 text-bleu" /> Échéances à venir</h2>
            <Link to="/dashboard/echeances" className="text-xs font-semibold text-bleu hover:underline" data-testid="home-deadlines-link">Gérer</Link>
          </div>
          <ul className="mt-4 space-y-2.5">
            {data.upcoming_deadlines.length === 0 && <li className="text-sm text-slate-500">Aucune échéance en attente.</li>}
            {data.upcoming_deadlines.map((d) => (
              <li key={d.id} className="flex items-center justify-between gap-3 rounded-xl border border-slate-100 p-3.5">
                <div className="min-w-0">
                  <p className="truncate text-[13px] font-semibold text-slate-900">{d.title}</p>
                  <p className="text-[11px] text-slate-400">{new Date(`${d.date}T00:00:00`).toLocaleDateString("fr-FR", { day: "numeric", month: "long" })}</p>
                </div>
                <span className={`shrink-0 rounded-full px-2.5 py-1 text-[10px] font-bold ${daysUntil(d.date) <= 7 ? "bg-rose-50 text-rouge" : daysUntil(d.date) <= 21 ? "bg-amber-50 text-ambre" : "bg-bleu-light text-bleu"}`}>
                  J-{daysUntil(d.date)}
                </span>
              </li>
            ))}
          </ul>
        </motion.div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        {[
          { icon: GraduationCap, title: "Coach civique (IA)", desc: "Révisez les valeurs, l'histoire et les institutions avec votre coach personnel.", to: "/dashboard/coach", color: "text-bleu", bg: "bg-bleu-light" },
          { icon: Bot, title: "Assistant démarches (IA)", desc: "Comprenez vos démarches, vos documents et les organismes concernés.", to: "/dashboard/assistant", color: "text-emeraude", bg: "bg-emerald-50" },
        ].map((c, i) => (
          <motion.div key={c.title} {...fade(7 + i)}>
            <Link to={c.to} className="card-integra card-hover flex items-start gap-4 p-6" data-testid={`home-ai-card-${i}`}>
              <span className={`rounded-2xl ${c.bg} p-3`}><c.icon className={`h-6 w-6 ${c.color}`} /></span>
              <div className="flex-1">
                <p className="flex items-center gap-2 text-sm font-bold text-slate-900">
                  {c.title}
                  <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide text-emeraude">Actif</span>
                </p>
                <p className="mt-1 text-xs leading-relaxed text-slate-500">{c.desc}</p>
              </div>
              <ArrowRight className="mt-1 h-4 w-4 shrink-0 text-slate-300" />
            </Link>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
